import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CardModule } from 'primeng/card';
import { KnobModule } from 'primeng/knob';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { FinanceStats, PaymentProgressStats, PaymentStats } from 'src/app/api/types/finance';
import { DashboardDataService } from '../../../../services/dashboard-data.service';
import { FinanceService } from 'src/app/api/finance.service';
import { IconService } from 'src/app/ui/icon.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TableCaptionComponent } from 'src/app/ui/tables/table-caption/table-caption.component';
import { Column, TableColumnsComponent, TableHeadersComponent } from 'src/app/ui/tables';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DateTime } from 'luxon';
import { UrlService } from 'src/app/services/url.service';
import { OverpaymentsGraphComponent } from '../overpayments-graph/overpayments-graph.component';

interface DisplayFinanceStats extends FinanceStats {
  contactCardLink: string;
}

@Component({
  selector: 'app-payment-progress',
  standalone: true,
  imports: [
    CardModule,
    KnobModule,
    FormsModule,
    TooltipModule,
    DecimalPipe,
    RouterModule,
    FontAwesomeModule,
    ButtonModule,
    DialogModule,
    TableModule,
    TableCaptionComponent,
    ProgressSpinnerModule,
    TableHeadersComponent,
    TableColumnsComponent,
    OverpaymentsGraphComponent
  ],
  templateUrl: './payment-progress.component.html',
  styleUrl: './payment-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentProgressComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  // Signals for payment data
  private paymentProgress = signal<PaymentProgressStats | null>(null);
  private yearlyPayments = signal<PaymentStats[]>([]);

  currentYear = 2026;
  currentMonth = new Date().toLocaleString('default', { month: 'long' });

  // Dialog state
  detailsDialogVisible = false;
  dialogTitle = '';
  exportFilename = '';
  overdueStats: any[] = [];
  monthStats: any[] = [];
  yearStats: any[] = [];
  financeStats = signal<DisplayFinanceStats[]>([]);
  dialogLoading = signal<boolean>(false);

  // Table columns definition
  tableColumns: Column<DisplayFinanceStats>[] = [
    { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
    { field: 'consultant', header: 'Consultant', type: 'text', sortable: true, filterable: true },
    { field: 'tripDate', header: 'Trip Date', type: 'date', sortable: true, filterable: true },
    { field: 'regDate', header: 'Registration Date', type: 'date', sortable: true, filterable: true },
    { field: 'tripId', header: 'Trip ID', type: 'number', sortable: true, filterable: true },
    { field: 'groupName', header: 'Group Name', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: true },
    { field: 'contactCardLink', header: 'Contact Card', type: 'text', hide: true },
    { field: 'groupLeader', header: 'Group Leader', type: 'text', sortable: true, filterable: true },
    { field: 'glEmail', header: 'GL Email', type: 'email', sortable: true, filterable: true },
    { field: 'glPhone', header: 'GL Phone', type: 'phone', sortable: true, filterable: true },
    { field: 'lastCommDate', header: 'Last Communication', type: 'date', sortable: true, filterable: true },
    { field: 'quickNote', header: 'Finance Note', type: 'text', sortable: true, filterable: true, columnClass: 'whitespace-nowrap max-w-sm text-ellipsis', includeTooltip: true, tooltipPosition: 'left' },
    { field: 'balance', header: 'Balance', type: 'currency', sortable: true, filterable: true },
    { field: 'dueDate', header: 'Due Date', type: 'date', sortable: true, filterable: true },
    { field: 'paymentType', header: 'Payment Type', type: 'text', sortable: true, filterable: true }
  ];

  // Computed values for each section
  pastDueProgress = computed(() => {
    const progress = this.paymentProgress();
    if (!progress) return { percentage: 0, value: 0, goal: 0, paidCount: 0, totalCount: 0 };
    const value = progress.pastDue.totalPaid;
    const goal = progress.pastDue.totalDue;
    const paidCount = progress.pastDue.paidCount || 0;
    const totalCount = progress.pastDue.paymentCount || 0;
    return {
      percentage: goal > 0 ? Math.round((value / goal) * 100) : 0,
      value,
      goal,
      paidCount,
      totalCount
    };
  });

  thisMonthProgress = computed(() => {
    const progress = this.paymentProgress();
    if (!progress) return { percentage: 0, value: 0, goal: 0, paidCount: 0, totalCount: 0 };
    const value = progress.thisMonth.totalPaid;
    const goal = progress.thisMonth.totalDue;
    const paidCount = progress.thisMonth.paidCount || 0;
    const totalCount = progress.thisMonth.paymentCount || 0;
    return {
      percentage: goal > 0 ? Math.round((value / goal) * 100) : 0,
      value,
      goal,
      paidCount,
      totalCount
    };
  });

  yearProgress = computed(() => {
    const progress = this.paymentProgress();
    if (!progress) return { percentage: 0, value: 0, goal: 0, paidCount: 0, totalCount: 0 };
    const value = progress.totalYear.totalPaid;
    const goal = progress.totalYear.totalDue;
    const paidCount = progress.totalYear.paidCount || 0;
    const totalCount = progress.totalYear.paymentCount || 0;
    return {
      percentage: goal > 0 ? Math.round((value / goal) * 100) : 0,
      value,
      goal,
      paidCount,
      totalCount
    };
  });

  constructor(
    private dashboardDataService: DashboardDataService,
    private financeService: FinanceService,
    public icons: IconService,
    private urlService: UrlService
  ) {
    // Load payment data with the correct year
    this.loadPaymentData();
  }

  ngOnInit() {
    // Initialize data loading
    this.dashboardDataService.initializeData();
  }

  private loadPaymentData() {
    // Load payment progress data with the correct year
    this.financeService.getPaymentProgress(this.currentYear)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(progress => {
        this.paymentProgress.set(progress);
      });

    // Load yearly payments data with the correct year
    this.financeService.getPaymentStatsGraphData(this.currentYear)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(payments => {
        this.yearlyPayments.set(payments);
      });
  }

  showDetailsDialog(type: 'overdue' | 'month' | 'year') {
    this.detailsDialogVisible = true;
    this.dialogLoading.set(true);

    switch (type) {
      case 'overdue':
        this.dialogTitle = 'Past Due Payments';
        this.exportFilename = 'past-due-payments';
        break;
      case 'month':
        this.dialogTitle = `Next 30 Days`;
        this.exportFilename = 'next-30-days-as-of' + DateTime.now().toFormat('yyyy-MM-dd');
        break;
      case 'year':
        this.dialogTitle = `${this.currentYear} Trip Payments`;
        this.exportFilename = 'yearly-payments';
        break;
    }

    this.getFinanceStats(type);
  }

  getFinanceStats(reportType: string) {
    this.financeService.getFinanceStats(reportType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(stats => {
        switch (reportType) {
          case 'overdue':
            this.overdueStats = stats;
            break;
          case 'month':
            this.monthStats = stats;
            break;
          case 'year':
            this.yearStats = stats;
            break;
        }
        this.financeStats.set(stats.map(stat => ({
          ...stat, contactCardLink: this.urlService.getContactCardUrl(stat.custId, { tripId: stat.tripId })
        })));
        this.dialogLoading.set(false);
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 